/**
 * Wishlist Quick Add to Cart
 * Handles variant selection, stock indicators, and AJAX cart additions
 */

(function() {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initStockBadges();
    initVariantSelectors();
    initQuickAddToCart();
    initMobileSwipeToRemove();
  }

  /**
   * Initialize stock status badges with contextual messaging
   */
  function initStockBadges() {
    const badges = document.querySelectorAll('.wishlist-item__stock-badge');
    
    badges.forEach(badge => {
      const status = badge.getAttribute('data-stock-status');
      const icon = badge.querySelector('.stock-badge__icon');
      const text = badge.querySelector('.stock-badge__text');
      
      switch(status) {
        case 'in_stock':
          badge.classList.add('stock-badge--in-stock');
          text.textContent = 'In Stock';
          break;
        case 'low_stock':
          badge.classList.add('stock-badge--low-stock');
          text.textContent = 'Low Stock';
          break;
        case 'out_of_stock':
          badge.classList.add('stock-badge--out-of-stock');
          text.textContent = 'Out of Stock';
          break;
      }
    });
  }

  /**
   * Initialize variant selectors for products with multiple options
   */
  function initVariantSelectors() {
    const selectors = document.querySelectorAll('.wishlist-item__variant-selector');
    
    selectors.forEach(selector => {
      const variantCount = parseInt(selector.getAttribute('data-variant-count'));
      
      // Only show variant selector if product has multiple variants
      if (variantCount > 1) {
        selector.style.display = 'block';
        const select = selector.querySelector('.variant-select');
        const productHandle = select.getAttribute('data-product-handle');
        
        // Fetch variants via product JSON
        fetchProductVariants(productHandle, select);
      }
    });
  }

  /**
   * Fetch product variants and populate selector
   */
  function fetchProductVariants(handle, selectElement) {
    fetch(`/products/${handle}?view=json`)
      .then(response => response.text())
      .then(data => {
        // Parse the product data
        const parts = data.split('~~');
        const variantCount = parseInt(parts[15]) || 1;
        
        if (variantCount > 1) {
          // Fetch full product JSON to get all variants
          fetch(`/products/${handle}.js`)
            .then(response => response.json())
            .then(product => {
              populateVariantSelector(selectElement, product.variants);
            })
            .catch(error => {
              console.error('[Wishlist] Error fetching product variants:', error);
            });
        }
      })
      .catch(error => {
        console.error('[Wishlist] Error fetching product data:', error);
      });
  }

  /**
   * Populate variant selector with options
   */
  function populateVariantSelector(selectElement, variants) {
    // Clear existing options except first
    selectElement.innerHTML = '<option value="">Select option...</option>';
    
    variants.forEach(variant => {
      if (variant.available) {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title + (variant.price ? ` - ${formatMoney(variant.price)}` : '');
        selectElement.appendChild(option);
      }
    });
    
    // Update add-to-cart button when variant is selected
    selectElement.addEventListener('change', function() {
      const button = this.closest('.wishlist-item__content').querySelector('.wishlist-item__add-to-cart');
      button.setAttribute('data-variant-id', this.value);
      button.disabled = !this.value;
    });
  }

  /**
   * Initialize quick add to cart functionality
   */
  function initQuickAddToCart() {
    const buttons = document.querySelectorAll('.wishlist-item__add-to-cart');
    
    buttons.forEach(button => {
      // Set initial button text based on stock status
      updateButtonText(button);
      
      button.addEventListener('click', function(e) {
        e.preventDefault();
        handleAddToCart(this);
      });
    });
  }

  /**
   * Update button text based on stock status
   */
  function updateButtonText(button) {
    const stockStatus = button.getAttribute('data-stock-status');
    const available = button.getAttribute('data-available') === 'true';
    const buttonText = button.querySelector('.button-text');
    
    if (!available || stockStatus === 'out_of_stock') {
      buttonText.textContent = 'Notify Me';
      button.classList.add('button--secondary');
      button.classList.remove('button--primary');
    } else if (stockStatus === 'low_stock') {
      buttonText.textContent = 'Add to Cart';
      // Keep urgency styling
    } else {
      buttonText.textContent = 'Add to Cart';
    }
  }

  /**
   * Handle add to cart action
   */
  function handleAddToCart(button) {
    const variantId = button.getAttribute('data-variant-id');
    const stockStatus = button.getAttribute('data-stock-status');
    const available = button.getAttribute('data-available') === 'true';
    
    // If out of stock, show notification signup
    if (!available || stockStatus === 'out_of_stock') {
      showNotificationSignup(button);
      return;
    }
    
    // Check if variant is selected (for multi-variant products)
    const variantSelector = button.closest('.wishlist-item__content').querySelector('.variant-select');
    if (variantSelector && variantSelector.style.display !== 'none' && !variantId) {
      alert('Please select an option first');
      variantSelector.focus();
      return;
    }
    
    // Show loading state
    button.disabled = true;
    button.querySelector('.button-text').style.display = 'none';
    button.querySelector('.button-loading').style.display = 'inline-flex';
    
    // Add to cart via AJAX
    addToCart(variantId, 1)
      .then(response => {
        // Success - show notification
        showSuccessNotification(button);
        
        // Update cart count in header
        updateCartCount();
        
        // Reset button after delay
        setTimeout(() => {
          button.disabled = false;
          button.querySelector('.button-text').style.display = 'inline';
          button.querySelector('.button-loading').style.display = 'none';
        }, 2000);
      })
      .catch(error => {
        console.error('[Wishlist] Add to cart error:', error);
        alert('Unable to add item to cart. Please try again.');
        
        // Reset button
        button.disabled = false;
        button.querySelector('.button-text').style.display = 'inline';
        button.querySelector('.button-loading').style.display = 'none';
      });
  }

  /**
   * Add item to cart via Shopify Cart API
   */
  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    });
  }

  /**
   * Update cart count in header
   */
  function updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCount = document.querySelector('.cart-count-bubble span');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
        }
      })
      .catch(error => {
        console.error('[Wishlist] Error updating cart count:', error);
      });
  }

  /**
   * Show success notification
   */
  function showSuccessNotification(button) {
    const card = button.closest('.wishlist-item');
    const notification = document.createElement('div');
    notification.className = 'wishlist-success-notification';
    notification.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
        <path d="M6 10L9 13L14 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span>Added to cart!</span>
    `;
    
    card.appendChild(notification);
    
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Show notification signup modal for out of stock items
   */
  function showNotificationSignup(button) {
    const productHandle = button.getAttribute('data-product-handle');
    // TODO: Implement back-in-stock notification signup
    alert('Back-in-stock notifications coming soon! We\'ll notify you when this item is available.');
  }

  /**
   * Initialize mobile swipe-to-remove gesture
   */
  function initMobileSwipeToRemove() {
    if (window.innerWidth > 749) return; // Desktop only
    
    const items = document.querySelectorAll('.wishlist-item');
    
    items.forEach(item => {
      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      
      item.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
        isDragging = true;
      });
      
      item.addEventListener('touchmove', function(e) {
        if (!isDragging) return;
        
        currentX = e.touches[0].clientX;
        const diff = currentX - startX;
        
        // Only allow left swipe
        if (diff < 0) {
          this.style.transform = `translateX(${diff}px)`;
        }
      });
      
      item.addEventListener('touchend', function(e) {
        if (!isDragging) return;
        isDragging = false;
        
        const diff = currentX - startX;
        
        // If swiped more than 100px, trigger remove
        if (diff < -100) {
          const removeButton = this.querySelector('.wishlist-item__remove a');
          if (removeButton) {
            removeButton.click();
          }
        }
        
        // Reset position
        this.style.transform = 'translateX(0)';
      });
    });
  }

  /**
   * Format money (simple version)
   */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

})();
